import { Injectable } from '@angular/core';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { environment } from '#environments';

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

interface LogEntry {
  ts: string;
  level: LogLevel;
  tag: string;
  message: string;
  data?: unknown;
}

const STORAGE_KEY = 'ares_app_log';
const MAX_ENTRIES = 500;

@Injectable({ providedIn: 'root' })
export class LoggingService {
  private _buffer: LogEntry[] = this._loadFromStorage();
  private _appInsights: ApplicationInsights | null = null;

  constructor() {
    if (environment.appInsightsConnectionString) {
      this._appInsights = new ApplicationInsights({
        config: {
          connectionString: environment.appInsightsConnectionString,
          enableAutoRouteTracking: true,
          disableFetchTracking: false,
        },
      });
      this._appInsights.loadAppInsights();
    }
  }

  /** Attaches the authenticated user identity to all subsequent App Insights telemetry. */
  setUser(userId: string, displayName: string): void {
    this._appInsights?.setAuthenticatedUserContext(userId, displayName, true);
  }

  /** Clears the user identity (on logout). */
  clearUser(): void {
    this._appInsights?.clearAuthenticatedUserContext();
  }

  debug(tag: string, message: string, data?: unknown): void {
    this._write('DEBUG', tag, message, data);
  }

  info(tag: string, message: string, data?: unknown): void {
    this._write('INFO', tag, message, data);
  }

  warn(tag: string, message: string, data?: unknown): void {
    this._write('WARN', tag, message, data);
  }

  error(tag: string, message: string, data?: unknown): void {
    this._write('ERROR', tag, message, data);
  }

  /** Downloads all buffered log entries as a plain-text app.log file. */
  download(): void {
    const lines = this._buffer.map(e => {
      const data = e.data !== undefined ? ` | ${JSON.stringify(e.data)}` : '';
      return `[${e.ts}] [${e.level.padEnd(5)}] [${e.tag}] ${e.message}${data}`;
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ares-app-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Clears the in-memory buffer and sessionStorage. */
  clear(): void {
    this._buffer = [];
    sessionStorage.removeItem(STORAGE_KEY);
  }

  private _write(level: LogLevel, tag: string, message: string, data?: unknown): void {
    const entry: LogEntry = {
      ts: new Date().toISOString(),
      level,
      tag,
      message,
      ...(data !== undefined && { data }),
    };

    this._buffer.push(entry);

    // Keep buffer within max size (drop oldest entries)
    if (this._buffer.length > MAX_ENTRIES) {
      this._buffer.splice(0, this._buffer.length - MAX_ENTRIES);
    }

    this._persist();
    this._sendToAppInsights(level, tag, message, data);

    // Mirror to browser console
    const line = `[${tag}] ${message}`;
    switch (level) {
      case 'DEBUG': console.debug(line, data ?? ''); break;
      case 'INFO': console.info(line, data ?? ''); break;
      case 'WARN': console.warn(line, data ?? ''); break;
      case 'ERROR': console.error(line, data ?? ''); break;
    }
  }

  private _sendToAppInsights(level: LogLevel, tag: string, message: string, data?: unknown): void {
    if (!this._appInsights) return;

    const properties: Record<string, string> = {
      tag,
      ...(data !== undefined && { data: JSON.stringify(data) }),
    };

    if (level === 'ERROR') {
      this._appInsights.trackException({
        exception: new Error(`[${tag}] ${message}`),
        severityLevel: SeverityLevel.Error,
        properties,
      });
    } else {
      const severityMap: Record<LogLevel, SeverityLevel> = {
        DEBUG: SeverityLevel.Verbose,
        INFO: SeverityLevel.Information,
        WARN: SeverityLevel.Warning,
        ERROR: SeverityLevel.Error,
      };
      this._appInsights.trackTrace({
        message: `[${tag}] ${message}`,
        severityLevel: severityMap[level],
        properties,
      });
    }
  }

  private _persist(): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this._buffer));
    } catch {
      // sessionStorage quota exceeded — trim and retry once
      this._buffer.splice(0, 50);
      try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this._buffer)); } catch { /* silent */ }
    }
  }

  private _loadFromStorage(): LogEntry[] {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as LogEntry[]) : [];
    } catch {
      return [];
    }
  }
}
